import Hls from 'hls.js';
import { useCallback, useEffect, useRef, useState } from 'react';

// ClipPlayer plays a single event_clip in the client portal.
//
// A saved clip is a standalone MP4 on disk (`preview_url`). That file does
// not depend on the live HLS URL or the match DVR still being up - if the
// stream dies after Save, the clip still plays. Only pass `renderedMp4Url`
// when it exists; never pair it with a live URL as a fallback.
//
// HLS (`hlsUrl` + start/end window) is last-resort only: clips that never
// got an extract (no chunks / ffmpeg miss). Prefer the local EVENT playlist
// over `matches.hls_stream_url` so a dead broadcaster feed cannot black out
// the library.

function cacheBust(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('_r', String(Date.now()));
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}_r=${Date.now()}`;
  }
}

export function ClipPlayer({
  hlsUrl, renderedMp4Url, startMs, endMs, poster, autoPlay = true, onEnded,
}: {
  hlsUrl?: string;
  renderedMp4Url?: string;
  startMs: number;
  endMs: number;
  poster?: string | null;
  autoPlay?: boolean;
  onEnded?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const endedRef = useRef(false);
  const onEndedRef = useRef(onEnded);
  const reloadGen = useRef(0);
  const [reloading, setReloading] = useState(false);
  onEndedRef.current = onEnded;

  useEffect(() => {
    endedRef.current = false;
    const v = videoRef.current;
    if (!v) return;
    hlsRef.current?.destroy();
    hlsRef.current = null;
    if (renderedMp4Url) {
      // A resave overwrites {uuid}.mp4 in place. React's `key=` gives us
      // a fresh <video> node, but setting `src` alone does not always
      // discard buffered metadata (Chrome will keep the old moov duration
      // if the previous src was the same origin). load() forces a full
      // teardown → new range request → new duration from the fresh moov.
      v.src = renderedMp4Url;
      v.load();
      // Force the very first painted frame to be MP4 frame 0. Chrome
      // sometimes paints a decoder warm-up frame that is not exactly
      // t=0 - especially with +faststart MOOV up front - so we seek to
      // 0 and wait for `seeked` before autoplay. Without this the
      // client's first visible frame can be one frame past the desk's IN.
      const armFirstFrame = () => {
        const startPlayback = () => {
          v.removeEventListener('seeked', startPlayback);
          if (autoPlay) void v.play().catch(() => {});
        };
        v.addEventListener('seeked', startPlayback);
        try { v.currentTime = 0; } catch { /* invalid currentTime must not crash */ }
      };
      v.addEventListener('loadedmetadata', armFirstFrame, { once: true });
      return () => {
        v.removeEventListener('loadedmetadata', armFirstFrame);
        v.removeAttribute('src');
        v.load();
      };
    }
    if (!hlsUrl) return;
    const seek = () => {
      const t = Number(startMs);
      try {
        v.currentTime = Number.isFinite(t) && t > 0 ? t / 1000 : 0;
      } catch { /* invalid currentTime must not crash the page */ }
      if (autoPlay) void v.play().catch(() => {});
    };
    if (v.canPlayType('application/vnd.apple.mpegurl')) {
      v.src = hlsUrl;
      v.addEventListener('loadedmetadata', seek, { once: true });
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        liveDurationInfinity: false,
        maxBufferHole: 0.8,
        maxStarvationDelay: 4,
      });
      hlsRef.current = hls;
      hls.loadSource(hlsUrl);
      hls.attachMedia(v);
      hls.on(Hls.Events.MANIFEST_PARSED, seek);
    }
    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
      v.removeAttribute('src');
      v.load();
    };
  }, [hlsUrl, renderedMp4Url, startMs, endMs, autoPlay]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const fireEnded = () => {
      if (endedRef.current) return;
      endedRef.current = true;
      onEndedRef.current?.();
    };
    const onNativeEnded = () => fireEnded();
    v.addEventListener('ended', onNativeEnded);
    if (renderedMp4Url) {
      return () => v.removeEventListener('ended', onNativeEnded);
    }
    const onTime = () => {
      const end = Number(endMs);
      if (Number.isFinite(end) && end > 0 && v.currentTime * 1000 >= end) {
        v.pause();
        v.currentTime = Math.max(0, startMs / 1000);
        fireEnded();
      }
    };
    v.addEventListener('timeupdate', onTime);
    return () => {
      v.removeEventListener('ended', onNativeEnded);
      v.removeEventListener('timeupdate', onTime);
    };
  }, [renderedMp4Url, startMs, endMs]);

  const reload = useCallback(() => {
    const v = videoRef.current;
    const src = renderedMp4Url || hlsUrl;
    if (!v || !src) return;
    const gen = ++reloadGen.current;
    setReloading(true);
    endedRef.current = false;
    const keep = renderedMp4Url ? (v.currentTime || 0) : Math.max(0, startMs / 1000);
    const wasPaused = v.paused;
    const next = cacheBust(src);
    const done = () => {
      if (gen !== reloadGen.current) return;
      try { v.currentTime = keep; } catch { /* ignore */ }
      if (!wasPaused || autoPlay) void v.play().catch(() => {});
      setReloading(false);
    };
    const hls = hlsRef.current;
    if (hls && hlsUrl && !renderedMp4Url) {
      const onParsed = () => {
        hls.off(Hls.Events.MANIFEST_PARSED, onParsed);
        done();
      };
      hls.on(Hls.Events.MANIFEST_PARSED, onParsed);
      try {
        hls.stopLoad();
        hls.loadSource(next);
        hls.startLoad();
      } catch {
        hls.off(Hls.Events.MANIFEST_PARSED, onParsed);
        setReloading(false);
      }
      window.setTimeout(() => {
        if (gen === reloadGen.current) setReloading(false);
      }, 8000);
      return;
    }
    const onMeta = () => {
      v.removeEventListener('loadedmetadata', onMeta);
      done();
    };
    v.addEventListener('loadedmetadata', onMeta);
    v.src = next;
    v.load();
  }, [autoPlay, hlsUrl, renderedMp4Url, startMs]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-lg bg-black">
      <video
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        poster={poster ?? undefined}
        className="min-h-0 w-full flex-1 bg-black object-contain"
      />
      <div className="flex shrink-0 items-center justify-end bg-[#0b0f16] px-2 py-1">
        <ReloadBtn spinning={reloading} onClick={reload} label={renderedMp4Url ? 'Reload video' : 'Reload playlist'} />
      </div>
    </div>
  );
}

export function ReloadableVideo({
  src, className, autoPlay, poster,
}: {
  src: string;
  className?: string;
  autoPlay?: boolean;
  poster?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reloadGen = useRef(0);
  const [reloading, setReloading] = useState(false);

  const reload = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const gen = ++reloadGen.current;
    setReloading(true);
    const keep = v.currentTime || 0;
    const wasPaused = v.paused;
    const onMeta = () => {
      v.removeEventListener('loadedmetadata', onMeta);
      if (gen !== reloadGen.current) return;
      try { v.currentTime = keep; } catch { /* ignore */ }
      if (!wasPaused || autoPlay) void v.play().catch(() => {});
      setReloading(false);
    };
    v.addEventListener('loadedmetadata', onMeta);
    v.src = cacheBust(src);
    v.load();
  }, [autoPlay, src]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-black">
      <video
        ref={videoRef}
        src={src}
        controls
        playsInline
        autoPlay={autoPlay}
        poster={poster}
        className={className ?? 'min-h-0 w-full flex-1 bg-black object-contain'}
      />
      <div className="flex shrink-0 items-center justify-end bg-[#0b0f16] px-2 py-1">
        <ReloadBtn spinning={reloading} onClick={reload} label="Reload video" />
      </div>
    </div>
  );
}

export function ReloadBtn({
  spinning, onClick, label = 'Reload',
}: {
  spinning?: boolean;
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={spinning}
      title={`${label} - no page refresh`}
      className="inline-flex h-7 items-center gap-1.5 rounded-md bg-white/10 px-2 text-[11px] font-semibold text-white ring-1 ring-white/10 hover:bg-white/20 disabled:opacity-70"
    >
      <span className={spinning ? 'animate-spin' : undefined}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
          <path d="M21 12a9 9 0 1 1-2.4-6M21 4v5h-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      Reload
    </button>
  );
}
